/**
 * Provider-agnostic geo helpers.
 *
 * These work purely on plain `{ lat, lng }` positions so they are shared by
 * every map client (Google, Hudhud, ...). Keeping them here means the marker
 * animation / heading logic lives in exactly one place regardless of provider.
 */

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

/**
 * Initial bearing from `from` to `to`, in degrees.
 * Matches Google's `geometry.spherical.computeHeading`: 0 = North, increasing
 * clockwise, range (-180, 180]. Consumers that need 0..360 normalise themselves.
 *
 * @param {{lat:number,lng:number}} from
 * @param {{lat:number,lng:number}} to
 * @returns {number}
 */
export function computeHeading(from, to) {
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  return toDeg(Math.atan2(y, x));
}

/**
 * Animate a captain marker from its current position to `toPos` over
 * `durationSec` seconds, calling `onFrame(heading)` on every frame so the
 * caller can swap a directional icon or rotate the marker.
 *
 * The marker only needs to satisfy the `CaptainMarker` contract
 * (`getPosition()` + `setPosition({lat,lng})`), so this is provider-agnostic.
 *
 * @param {{getPosition:Function,setPosition:Function}} marker
 * @param {{lat:number,lng:number}} toPos
 * @param {number} durationSec
 * @param {(heading:number)=>void} [onFrame]
 */
export function animateMarker(marker, toPos, durationSec, onFrame) {
  const from = marker.getPosition();
  if (!from) return;

  cancelMarkerAnimation(marker);

  const deltaLat = toPos.lat - from.lat;
  const deltaLng = toPos.lng - from.lng;

  // Nothing actually moved (or no real duration): snap and skip the rAF loop.
  if ((deltaLat === 0 && deltaLng === 0) || !(durationSec > 0)) {
    marker.setPosition(toPos);
    return;
  }

  const start = performance.now();

  const step = (now) => {
    const elapsed = (now - start) / 1000;
    const progress = Math.min(elapsed / durationSec, 1);

    const cur = {
      lat: from.lat + deltaLat * progress,
      lng: from.lng + deltaLng * progress,
    };
    const prev = {
      lat: from.lat + deltaLat * Math.max(progress - 0.01, 0),
      lng: from.lng + deltaLng * Math.max(progress - 0.01, 0),
    };

    const heading = computeHeading(prev, cur);

    marker.setPosition(cur);
    if (onFrame) onFrame(heading);

    if (progress < 1) {
      marker._animFrame = requestAnimationFrame(step);
    } else {
      marker._animFrame = null;
    }
  };

  marker._animFrame = requestAnimationFrame(step);
}

/**
 * Stop any in-flight `animateMarker` loop on a marker. Call it before removing a marker or tearing down a map
 * @param {object} marker
 */
export function cancelMarkerAnimation(marker) {
  if (marker && marker._animFrame) {
    cancelAnimationFrame(marker._animFrame);
    marker._animFrame = null;
  }
}

/**
 * Approximate bounding box of Saudi Arabia (padded slightly). Used to decide
 * whether Hudhud (Saudi-only coverage) can serve a set of points, or whether we
 * should fall back to a global provider (Google).
 */
export const SAUDI_BOUNDS = {
  minLat: 16.0,
  maxLat: 33.0,
  minLng: 34.0,
  maxLng: 56.0,
};

/** True if a single { lat, lng } point is within the Saudi bounding box. */
export function isInSaudi(pos) {
  if (!pos || typeof pos.lat !== "number" || typeof pos.lng !== "number") {
    return false;
  }
  return (
    pos.lat >= SAUDI_BOUNDS.minLat &&
    pos.lat <= SAUDI_BOUNDS.maxLat &&
    pos.lng >= SAUDI_BOUNDS.minLng &&
    pos.lng <= SAUDI_BOUNDS.maxLng
  );
}

/** True only if there is at least one point and every point is within Saudi. */
export function allInSaudi(points) {
  return (
    Array.isArray(points) && points.length > 0 && points.every(isInSaudi)
  );
}
